import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '#lib/components/dialog.jsx';
import Button from '#lib/components/button.jsx';
import checkbox from '#lib/components/checkbox.jsx';
import { toast } from 'react-hot-toast';

import { addClasses, mergeClasses, cn } from '#lib/helpers/class.js';

import { AddRowButton, GetCitiesButton, Row } from '../@itinerary/planner/row.jsx';
import { deletePlaceToRegenerate, ungroup } from '#@itinerary/places/helpers.js';
import { Icon } from '@iconify-icon/react';
import FloatingTooltip from './tooltip.jsx';
import { XMarkIcon } from '@heroicons/react/24/solid';

const { Full: Checkbox } = checkbox;

export function FiltersModal(props) {
    const {
        itineraryId,
        isOpen,
        onClose,
        isCreating,
        handleIsCreating,
        places,
        deletePlace,
        updatePlaces,
        plans,
        totalFilters,
        placeFiltersProps, // destrutturo placeFiltersProps
        language
    } = props;

    const [showWarning, setShowWarning] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const {
        initialState,
        placeFilters,
        addPlaceFilter,
        deletePlaceFilter,
        editPlaceFilter,
        editFilter,
        editMacroFilter,
        getCities,
        selectedPlaceFilterIndex,
        setSelectedPlaceFilterIndex
    } = placeFiltersProps;

    /////////////////////////////////////
    // HANDLERS

    const handleDeletePlace = (idx) => {
        confirm(_t.global.confirmDelete()) && deletePlaceFilter(idx);
    };

    const handleGetCities = () => {
        const confirm = window.confirm(_t.edit.places.confirmGetCities());
        if (!confirm) return;
        const plansCities = plans.map((plan) => plan?.city).filter((el) => el);
        const placesCities = places.map((place) => place?.name).filter((el) => el);
        const cities = new Set(plansCities.concat(placesCities));
        if (!cities || cities.length === 0) {
            toast.error(_t.edit.filters.noCities());
            return;
        }
        getCities(places, plansCities);
    };

    const handleRegenerate = useCallback(
        (shouldRegenerateLocked) => {
            if (isCreating) {
                toast.error(_t.edit.filters.isCreating());
                return false;
            }
            handleIsCreating(() => true);
            placeFilters.forEach((pf) => {
                if (pf.empty) delete pf.empty;
            });
            placeFilters.forEach((pf) => {
                if (!pf.regenerate) pf.regenerate = false;
            });
            deletePlaceToRegenerate(places, placeFilters, deletePlace, shouldRegenerateLocked);
            updatePlaces.mutate({
                id: itineraryId,
                places: ungroup(places),
                placeFilters,
                language: _t.intl[language]().toLowerCase()
            });
            return true;
        },
        [placeFilters, updatePlaces]
    );
    /////////////////////////////////////

    return (
        <Dialog isOpen={isOpen} onClose={onClose}>
            <Dialog.Panel className="flex flex-col space-y-6 w-[90%] max-h-[80%] mt-28 overflow-auto">
                <div className="pb-2 pr-4">
                    <Dialog.CloseButton />
                    <b>{_t.edit.filters.filtersText()}</b>
                    <p className="text-sm text-neutral-400">{_t.edit.filters.filtersDescription()}</p>
                </div>

                {showWarning && (
                    <div className="relative flex items-center justify-center p-4 rounded-lg bg-amber-100">
                        <span>{_t.edit.filters.warning()}</span>
                        <Button
                            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-[50%] hover:font-semibold"
                            onClick={() => setShowWarning(false)}
                        >
                            <XMarkIcon className="icon text-neutral-500 hover:text-neutral-900" />
                        </Button>
                    </div>
                )}

                <div className="flex flex-col space-y-4">
                    {placeFilters?.map((pf, pfIndex) => {
                        const isInfoActive = pf.filters?.some((filter) => filter.type === 'info');
                        const isSuggestionsActive = pf.filters?.some((filter) => filter.type === 'suggestion');
                        const selected = pfIndex === selectedPlaceFilterIndex;
                        return (
                            <ItemRow
                                key={pfIndex}
                                totalFilters={totalFilters}
                                emptyPlaceFilter={pf.empty}
                                name={pf.name}
                                index={pfIndex}
                                selected={selected}
                                onSelect={setSelectedPlaceFilterIndex}
                                onDelete={handleDeletePlace}
                                onEdit={editPlaceFilter}
                                regenerate={pf.regenerate}
                                setShowWarning={setShowWarning}
                                submitted={submitted}
                            >
                                <div className="flex flex-col w-full p-2 space-y-5 rounded-b bg-neutral-50">
                                    <div className="flex flex-col space-y-1">
                                        <FiltersDetail
                                            placeIndex={pfIndex}
                                            places={placeFilters}
                                            totalFilters={totalFilters}
                                            typeChecked={isInfoActive}
                                            onEditAll={() =>
                                                editMacroFilter(totalFilters, pfIndex, 'info', !isInfoActive)
                                            }
                                            onEdit={editFilter}
                                            field="info"
                                            type="info"
                                        />
                                    </div>

                                    <div className="flex flex-col space-y-1">
                                        <FiltersDetail
                                            placeIndex={pfIndex}
                                            places={placeFilters}
                                            totalFilters={totalFilters}
                                            typeChecked={isSuggestionsActive}
                                            onEditAll={() =>
                                                editMacroFilter(
                                                    totalFilters,
                                                    pfIndex,
                                                    'suggestion',
                                                    !isSuggestionsActive
                                                )
                                            }
                                            onEdit={editFilter}
                                            field="suggestion"
                                            type="suggestions"
                                        />
                                    </div>
                                </div>
                            </ItemRow>
                        );
                    })}
                </div>
                <div className="flex justify-center w-full gap-8">
                    <AddRowButton
                        onClick={() => {
                            addPlaceFilter();
                            setSubmitted(false);
                        }}
                        style={{
                            maxWidth: '300px'
                        }}
                    />
                    <GetCitiesButton
                        onClick={handleGetCities}
                        style={{
                            maxWidth: '300px'
                        }}
                    >
                        <span>{_t.components.modal.getCities()}</span>
                    </GetCitiesButton>
                </div>
                <div className="flex justify-end gap-x-4">
                    <Button
                        onClick={() => {
                            const hasNoPlaceFilters = !placeFilters?.some((el) => el.filters.length > 0);
                            const emptyPlaceName = placeFilters?.some((el) => !el.name);
                            switch (true) {
                                case placeFilters.length === 0:
                                    toast.error('Add a place to generate');
                                    return;
                                case hasNoPlaceFilters:
                                    toast.error('No filter selected');
                                    return;
                                case emptyPlaceName:
                                    toast.error('Place name is required');
                                    return;
                                default:
                            }
                            //TODO diff tra initial state e placeFilter, SE ci sono solo filtri nuovi non prompt
                            const updatedState = placeFilters.filter((filter) =>
                                initialState?.some((state) => state.name === filter.name)
                            );
                            let shouldRegenerateLocked = false;
                            if (updatedState.length > 0 && updatedState.some((place) => place.regenerate)) {
                                const confirm = window.confirm(_t.edit.filters.regenerateWarning());
                                if (!confirm) return;
                                if (places.some((place) => place.locked))
                                    shouldRegenerateLocked = window.confirm(_t.edit.filters.confirmRegenerateLocked());
                            }

                            setShowWarning(false);
                            setSubmitted(true);
                            const regenerate = handleRegenerate(shouldRegenerateLocked);
                            if (regenerate) onClose();
                        }}
                        disabled={!placeFilters || placeFilters.length === 0}
                        className={addClasses('flex items-center justify-center')}
                        size="xs"
                    >
                        <Icon icon="line-md:upload-outline-loop" width="1.2rem" height="1.2rem" className="mr-2" />
                        <span>{_t.global.generate()}</span>
                    </Button>
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}

function FiltersDetail(props) {
    const { placeIndex, places, totalFilters, type, typeChecked, onEditAll, onEdit, field } = props;

    const place = places[placeIndex];
    const placeFilters = place?.filters;
    const filteredPlaceFilters = placeFilters?.filter((filter) => filter?.type === field);
    const filteredTotalFilters = totalFilters?.filter((filter) => filter?.type === field);

    const handleCheckbox = (placeIndex, filterId, filterName) => {
        onEdit(placeIndex, field, filterId, filterName);
    };

    return (
        <div className="flex gap-4">
            <div className="flex w-[60%] sm:w-[40%] md:w-[30%] lg:w-[20%]">
                <Checkbox checked={typeChecked} className="mr-2" onChange={onEditAll} />
                <FloatingTooltip content={_t.edit.filters.selectAll()}>
                    <b>{_t.edit.filters[type]()}:</b>
                </FloatingTooltip>
            </div>
            <div className="grid flex-wrap w-full grid-cols-1 gap-y-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
                {filteredTotalFilters?.map((filter) => {
                    let checked = false;
                    if (filteredPlaceFilters) {
                        checked = filteredPlaceFilters.find((f) => f.id === filter.id) ? true : false;
                    }
                    return (
                        <Checkbox
                            key={filter.id}
                            label={filter.name}
                            labelClassName="cursor-pointer"
                            checked={checked}
                            className="flex items-center col-span-1 gap-x-2 "
                            onChange={() => handleCheckbox(placeIndex, filter.id, filter.name)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function ItemRow(props) {
    const {
        as = 'li',
        name,
        emptyPlaceFilter,
        index,
        selected,
        onSelect,
        onDelete,
        onEdit,
        regenerate,
        setShowWarning,
        style,
        children,
        submitted
    } = props;

    const handleSelect = React.useCallback(() => {
        onSelect(index);
    }, [onSelect, index]);

    return (
        <Row
            as={as}
            style={{ zIndex: selected ? 10 : 0, ...style }}
            className={addClasses('relative flex-col pr-1 gap-0')}
            onClick={handleSelect}
        >
            <div className="flex items-center justify-between w-full p-1 rounded">
                <div className="flex items-center justify-start w-1/3 gap-x-2">
                    <InputText
                        value={name}
                        placeholder={_t.edit.places.newPlace()}
                        onEdit={(data) => {
                            if (!emptyPlaceFilter) setShowWarning(true);
                            onEdit(index, 'name', data);
                        }}
                        submitted={submitted}
                        className={addClasses('pl-1 text-lg font-bold')}
                    />
                </div>
                <div className="flex items-center gap-x-3">
                    {emptyPlaceFilter || (
                        <FloatingTooltip content={_t.edit.filters.regenerateDescription()}>
                            <Checkbox
                                label={_t.global.regenerate()}
                                checked={regenerate}
                                onChange={() => onEdit(index, 'regenerate', !regenerate)}
                            />
                        </FloatingTooltip>
                    )}
                    <FloatingTooltip content={_t.global.delete()}>
                        <Icon
                            icon="ph:trash"
                            width="1.3rem"
                            height="1.3rem"
                            className={'pt-2 cursor-pointer'}
                            onClick={() => {
                                onDelete(name, index);
                            }}
                        />
                    </FloatingTooltip>
                </div>
            </div>
            {children}
        </Row>
    );
}

function InputText(prop) {
    const { value, placeholder, onEdit, className, submitted } = prop;
    return (
        <input
            type="text"
            className={mergeClasses(
                cn(
                    value || !submitted
                        ? 'py-1 w-full text-ellipsis border-b border-gray-300 focus:text-neutral-800 focus:font-normal focus:border-gray-500 focus:outline-none'
                        : 'py-1 w-full text-ellipsis bg-red-50 border-b border-red-300 hover:border-red-500 focus:text-neutral-800 focus:font-normal focus:border-red-500 focus:outline-none'
                ),
                className
            )}
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
                onEdit(e.target.value);
            }}
        />
    );
}
